import { useState } from "react";
import { useFormField } from "../../hooks/useFormField";
import { FieldHint } from "../FieldHint";
import { inputClass, labelClass, resolveFieldState } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type NumberField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends number | null ? K : never;
}[keyof IntakeFormData];

interface NumberInputProps {
  field: NumberField;
  label: string;
  validation: ReturnType<typeof useStepValidation>;
  required?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  /** Static helper text rendered between the label and the input. */
  hint?: string;
  containerClassName?: string;
}

export function NumberInput({
  field,
  label,
  validation,
  required,
  placeholder,
  min,
  max,
  maxLength,
  hint,
  containerClassName,
}: NumberInputProps) {
  const [touched, setTouched] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const f = useFormField(field, validation);
  const display = f.value === null || f.value === undefined ? "" : String(f.value);
  // Suppress all error feedback while the field is focused — only show on blur.
  const activeError = isEditing ? null : f.error;
  const state = resolveFieldState({
    error: activeError,
    isMismatch: !!f.mismatch,
    isPrefilled: f.isPrefilled,
  });

  return (
    <div className={containerClassName}>
      <label htmlFor={field} className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <input
        id={field}
        type="text"
        inputMode="numeric"
        value={display}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "");
          const capped = maxLength !== undefined ? digits.slice(0, maxLength) : digits;
          const next = capped === "" ? null : parseInt(capped, 10);
          f.setValue(next as IntakeFormData[typeof field]);
        }}
        onFocus={() => setIsEditing(true)}
        onBlur={() => { setIsEditing(false); setTouched(true); f.onBlur(); }}
        placeholder={placeholder}
        maxLength={maxLength}
        className={inputClass(state)}
      />
      <FieldHint error={activeError} isPrefilled={f.isPrefilled} mismatch={f.mismatch} />
      {touched && !isEditing && min !== undefined && !activeError && f.value !== null && f.value !== undefined && f.value < min && (
        <p className="text-xs text-warning mt-1">Must be at least {min}</p>
      )}
      {touched && !isEditing && max !== undefined && !activeError && f.value !== null && f.value !== undefined && f.value > max && (
        <p className="text-xs text-warning mt-1">Must be at most {max}</p>
      )}
    </div>
  );
}
